import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { InnerLayout, PillNavItem, HeaderActionItem } from '../components/layout/InnerLayout';
import { Button } from '../components/ui/Button';
import {
  User,
  Lock,
  CreditCard,
  Building2,
  Plus,
  Crown,
  Receipt,
} from 'lucide-react';
import { AccountViewProps, AccountTabKey, BillingSubTabKey } from './account/types';
import { AccountGeneralTab } from './account/AccountGeneralTab';
import { AccountSecurityTab } from './account/AccountSecurityTab';
import { AccountOrganizationsTab } from './account/AccountOrganizationsTab';
import { AccountBillingTab } from './account/AccountBillingTab';
import { CreateOrgModal } from './account/modals/CreateOrgModal';

export const AccountView: React.FC<AccountViewProps> = ({ section: propSection }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { organizations } = useAuth();

  const activeTab: AccountTabKey = useMemo(() => {
    if (propSection) return propSection;
    const path = location.pathname;
    if (path.includes('/security')) return 'security';
    if (path.includes('/billing') || path.includes('/subscriptions')) return 'billing';
    if (path.includes('/organizations')) return 'organizations';
    const tabParam = searchParams.get('tab');
    if (tabParam === 'security' || tabParam === 'billing' || tabParam === 'organizations' || tabParam === 'subscriptions') {
      if (tabParam === 'subscriptions') return 'billing';
      return tabParam as AccountTabKey;
    }
    return 'general';
  }, [propSection, location.pathname, searchParams]);

  // Billing sub-tab: 'subscriptions' | 'billing'
  const [billingSubTab, setBillingSubTab] = useState<BillingSubTabKey>(() => {
    if (location.pathname.includes('/subscriptions')) return 'subscriptions';
    const tabParam = searchParams.get('tab');
    if (tabParam === 'billing' || tabParam === 'invoices') return 'billing';
    if (tabParam === 'subscriptions') return 'subscriptions';
    return 'subscriptions';
  });

  useEffect(() => {
    if (location.pathname.includes('/subscriptions')) {
      setBillingSubTab('subscriptions');
    } else {
      const tabParam = searchParams.get('tab');
      if (tabParam === 'billing' || tabParam === 'invoices') {
        setBillingSubTab('billing');
      } else if (tabParam === 'subscriptions') {
        setBillingSubTab('subscriptions');
      }
    }
  }, [location.pathname, searchParams]);

  const handleSelectBillingSubTab = (subTab: BillingSubTabKey) => {
    setBillingSubTab(subTab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', subTab);
    navigate({ pathname: '/account/billing', search: newParams.toString() }, { replace: true });
  };

  const [createOrgOpen, setCreateOrgOpen] = useState(false);

  let headerTitle: React.ReactNode = 'Profile & General';
  let headerDescription: React.ReactNode =
    'Manage your personal display identity, credentials, and user metadata.';
  let headerActions: HeaderActionItem[] | React.ReactNode = undefined;
  let navItems: PillNavItem[] | undefined = undefined;

  if (activeTab === 'security') {
    headerTitle = 'Security & Credentials';
    headerDescription =
      'Ensure your account is protected with strong password authentication and active session security.';
  } else if (activeTab === 'billing') {
    headerTitle = 'Billing & Subscriptions';
    headerDescription =
      'Cross-organization subscription plans, renewal timelines, and complete billing transactions.';
    navItems = [
      {
        id: 'subscriptions',
        label: 'Subscriptions',
        icon: Crown,
        badge: (
          <span className="px-1.5 py-0.5 rounded bg-cf-bg border border-cf-border text-cf-textSecondary text-xs font-mono">
            {organizations.length}
          </span>
        ),
        active: billingSubTab === 'subscriptions',
        onClick: () => handleSelectBillingSubTab('subscriptions'),
      },
      {
        id: 'billing',
        label: 'Billing',
        icon: Receipt,
        active: billingSubTab === 'billing',
        onClick: () => handleSelectBillingSubTab('billing'),
      },
    ];
  } else if (activeTab === 'organizations') {
    headerTitle = `My Organizations (${organizations.length})`;
    headerDescription =
      'Organizations are isolated containers for your projects, tables, and team members.';
    headerActions = [
      {
        label: 'Create Organization',
        icon: Plus,
        variant: 'primary',
        onClick: () => setCreateOrgOpen(true),
      },
    ];
  }

  // On mobile (< md) when not in billing, provide top-level account navigation pills
  if ((activeTab as string) !== 'billing') {
    navItems = [
      {
        id: 'general',
        label: 'Profile',
        icon: User,
        active: activeTab === 'general',
        href: '/account?tab=general',
        className: 'md:hidden',
      },
      {
        id: 'security',
        label: 'Security',
        icon: Lock,
        active: activeTab === 'security',
        href: '/account?tab=security',
        className: 'md:hidden',
      },
      {
        id: 'billing',
        label: 'Billing',
        icon: CreditCard,
        active: (activeTab as string) === 'billing',
        href: '/account?tab=billing',
        className: 'md:hidden',
      },
      {
        id: 'organizations',
        label: `Organizations (${organizations.length})`,
        icon: Building2,
        active: activeTab === 'organizations',
        href: '/account?tab=organizations',
        className: 'md:hidden',
      },
    ];
  }

  return (
    <InnerLayout
      header={{
        title: headerTitle,
        description: headerDescription,
        actions: headerActions,
      }}
      navItems={navItems}
    >
      {activeTab === 'general' && <AccountGeneralTab />}
      {activeTab === 'security' && <AccountSecurityTab />}
      {activeTab === 'billing' && (
        <AccountBillingTab
          billingSubTab={billingSubTab}
          onCreateOrgClick={() => setCreateOrgOpen(true)}
        />
      )}
      {activeTab === 'organizations' && (
        <AccountOrganizationsTab onCreateOrgClick={() => setCreateOrgOpen(true)} />
      )}

      <CreateOrgModal
        isOpen={createOrgOpen}
        onClose={() => setCreateOrgOpen(false)}
      />
    </InnerLayout>
  );
};
